import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/**
 * A competência — o mês de referência — escrita por extenso.
 *
 * `2026-05` é o formato do contrato de dados: é o que a API recebe na rota
 * (`/api/v1/fiscalizacao/2026-05`), o que vai na query string e o que o nome do arquivo da planilha
 * carrega ("Ref 2026-05"). Não é o formato de ninguém ler. Quem assina o termo de recebimento lê
 * "Maio de 2026", e a tradução acontece só na saída — o valor que viaja continua sendo o mesmo.
 *
 * A data é construída no fuso local e nunca convertida: o dia 1º às 00:00 locais formatado com o
 * mesmo fuso não cruza fronteira de dia, e por isso não há aqui o deslocamento de três horas que
 * decide a glosa de quem fecha na borda do mês. Esse cálculo é do servidor, e é lá que ele fica.
 *
 * @param competencia o mês no formato `AAAA-MM`
 * @returns o mês por extenso, com inicial maiúscula; ou o próprio texto quando ele não é uma
 *     competência — inventar "Janeiro de 1970" a partir de lixo seria pior que mostrar o lixo
 */
export function competenciaPorExtenso(competencia: string): string {
  const partes = /^(\d{4})-(\d{2})$/.exec(competencia)
  if (!partes) {
    return competencia
  }
  const ano = Number(partes[1])
  const mes = Number(partes[2])
  if (mes < 1 || mes > 12) {
    return competencia
  }
  const escrito = format(new Date(ano, mes - 1, 1), "MMMM 'de' yyyy", { locale: ptBR })
  return escrito.charAt(0).toUpperCase() + escrito.slice(1)
}

/**
 * O período de referência da competência, como o termo de recebimento o escreve.
 *
 * O TRP nº 01 imprime "06/05/2026 a 31/05/2026" — e o primeiro dia não é o dia 1º, porque o
 * contrato começou no meio do mês. **Este cálculo não sabe disso, e não deve saber:** o dia de
 * início do contrato é dado, não constante de código, e não está modelado em lugar nenhum. Aqui sai
 * o mês inteiro; quem emite o termo do mês inicial ou final corrige a data à mão, e a ressalva do
 * documento diz que a quantidade proporcional de dias não é gerada pelo sistema.
 *
 * @param competencia o mês no formato `AAAA-MM`
 * @returns o intervalo em pt-BR, ou o próprio texto quando ele não é uma competência
 */
export function periodoDaCompetencia(competencia: string): string {
  const partes = /^(\d{4})-(\d{2})$/.exec(competencia)
  if (!partes) {
    return competencia
  }
  const ano = Number(partes[1])
  const mes = Number(partes[2])
  if (mes < 1 || mes > 12) {
    return competencia
  }
  const primeiro = new Date(ano, mes - 1, 1)
  const ultimo = new Date(ano, mes, 0)
  return `${format(primeiro, 'dd/MM/yyyy')} a ${format(ultimo, 'dd/MM/yyyy')}`
}
